/**
 * Sentinel "start of time" for the Shared Rebate Programs date filter
 * (PART-4130, requester-confirmed 2026-08-20). Comfortably before any real
 * Master IB program could exist. Shared by the toolbar's "All time" shortcut
 * (`SharedRebateProgramsView.tsx`) and the portal's `toDateParams` fallback
 * so there's exactly one literal, not several independently-typed copies.
 */
export const ALL_TIME_START = new Date(2021, 0, 1);

/**
 * The date range the **Sub IBs tab** (`SubIBReportProvider`) opens on: Last
 * 30 days, resolved against the real "today" rather than an arbitrary/empty
 * range.
 *
 * Confirmed design default (`Datepicker.tsx` `DR_DEFAULT()`). It deliberately
 * lines up with `DatepickerV2`'s built-in "Last 30 days" shortcut period, so
 * `getShortcutButtonLabel` collapses it to the preset name in the picker's
 * trigger instead of showing two raw dates. Changing the span here without
 * changing that shortcut would silently break the label.
 *
 * **Not** the Shared Rebate Programs tab's default — see `getAllTimeDateRange`
 * below (PART-4130 changed only that tab's default; the Sub IBs tab, a
 * separate feature sharing this file, is untouched).
 */
export const getDefaultDateRange = (): { from: Date; to: Date } => {
  const to = new Date();
  to.setHours(0, 0, 0, 0);
  const from = new Date(to);
  from.setDate(from.getDate() - 30);
  return { from, to };
};

/**
 * Same default in `DatepickerV2`'s own `DateValueType` shape, for the Sub
 * IBs tab drawer's local date-range chip (`SubIBDetailDrawer.tsx`). Must seed
 * real dates rather than `null`: a null value leaves the calendar and its
 * shortcut list with nothing selected when it opens, even though the trigger
 * reads "Last 30 days".
 */
export const getDefaultDateValue = (): { startDate: Date; endDate: Date } => {
  const { from, to } = getDefaultDateRange();
  return { startDate: from, endDate: to };
};

/**
 * The date range the **Shared Rebate Programs** tab (desktop + mobile) opens
 * on: **All time** — no bounded window (PART-4130, PM logic 18 Aug 2026: the
 * report defaults to all-time full-history figures).
 *
 * This deliberately lines up with the toolbar's own "All time" custom
 * shortcut period (`SharedRebateProgramsView.tsx`), so `getShortcutButtonLabel`
 * collapses it to that preset name rather than showing two raw dates —
 * changing the span here without changing that shortcut would silently break
 * the label, same dependency `getDefaultDateRange` documents for the Sub IBs
 * tab's own default above.
 */
export const getAllTimeDateRange = (): { from: Date; to: Date } => {
  const to = new Date();
  to.setHours(0, 0, 0, 0);
  return { from: ALL_TIME_START, to };
};

/**
 * Same default in `DatepickerV2`'s own `DateValueType` shape, for the
 * program detail drawer's local date-range chip (`ProgramDetailsDrawer.tsx`)
 * when it isn't seeded from a page-level `pageDateRange` prop.
 */
export const getAllTimeDateValue = (): { startDate: Date; endDate: Date } => {
  const { from, to } = getAllTimeDateRange();
  return { startDate: from, endDate: to };
};
